// Layer 3 (Transformations): cursor-relative labels and shift-row derivation
// shared across the five glass landings (decisions.md § recovery basis, D1).

import type { CrewId } from '@/versions/v1/schema/seat';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { activeCrewForShift } from '@/versions/v1/shared/bunker-hill-org-seed';
import {
  addMinutes,
  diffMinutes,
  formatClockShort,
  parseTime,
  productionDayOf,
  shiftOf,
} from '@/versions/v1/shared/clock.derive';
import { seriesAt } from '@/versions/v1/shared/series.derive';
import { JUNE_DAILY_PLAN_DST, juneMtdDays } from '@/versions/v1/shared/june-tonnage.derive';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const CREW_PANEL: Record<CrewId, 'A' | 'B' | 'C' | 'D'> = {
  'crew-a': 'A',
  'crew-b': 'B',
  'crew-c': 'C',
  'crew-d': 'D',
};

/** Compact date label from a series stamp — e.g. `8 Jun`. */
export function formatShiftDateLabel(at: SeriesTime): string {
  const { date } = productionDayOf(at);
  const d = parseTime(`${date}T12:00`);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** 12h composite label — e.g. `08 Jun 09:00 (latest)` on the cursor shift. */
export function formatCompositeLabel(at: SeriesTime, cursor: SeriesTime, isLatest: boolean): string {
  const { date: stampDay } = productionDayOf(at);
  const { date: cursorDay } = productionDayOf(cursor);
  const d = parseTime(at);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const mon = MONTHS[d.getUTCMonth()];
  const hm = at.split('T')[1] ?? '00:00';
  const suffix = isLatest && stampDay === cursorDay ? ' (latest)' : '';
  return `${day} ${mon} ${hm}${suffix}`;
}

/** Month-to-date label from the cursor production day — e.g. `Jun to date`. */
export function cursorMonthToDateLabel(state: SessionState): string {
  const { date } = productionDayOf(state.cursor);
  const d = parseTime(`${date}T12:00`);
  return `${MONTHS[d.getUTCMonth()]} to date`;
}

/** Calendar date offset from the cursor production day — e.g. `18 Jun` or `8 Jun 2026`. */
export function formatDateOffsetFromCursor(
  state: SessionState,
  offsetDays: number,
  opts?: { includeYear?: boolean },
): string {
  const { date } = productionDayOf(state.cursor);
  const due = addMinutes(`${date}T12:00`, offsetDays * 24 * 60);
  const d = parseTime(due);
  const base = `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
  return opts?.includeYear ? `${base} ${d.getUTCFullYear()}` : base;
}

/** Next 12h LIMS composite due label and last-result caption from shift stamps. */
export function nextCompositeDueLabel(state: SessionState): { value: string; caption: string } {
  const latest = lastCompositeStamps(state, 1)[0] ?? state.cursor;
  const nextAt = addMinutes(latest, 12 * 60);
  const cursorCalDate = state.cursor.split('T')[0] ?? '';
  const nextCalDate = nextAt.split('T')[0] ?? '';
  const nextHm = nextAt.split('T')[1] ?? '00:00';
  const lastHm = latest.split('T')[1] ?? '00:00';
  // The Control metallurgist's header fact (polish.P26 adjudication 6): the
  // next composite's clock, dated only when it falls after today, and the last
  // result's stamp in the top bar's `08 Jun` spelling.
  const padded = (at: SeriesTime) => formatShiftDateLabel(at).replace(/^(\d) /, '0$1 ');
  const value = nextCalDate === cursorCalDate ? nextHm : `${nextHm} · ${padded(nextAt)}`;
  const caption = `last result ${lastHm} · ${padded(latest)}`;
  return { value, caption };
}

/** Due label relative to the session cursor — `Today 14:00`, `+3d 18 Sep`, `Overdue`. */
export function formatDueFromCursor(
  state: SessionState,
  opts: { kind: 'today'; hm: string } | { kind: 'offset'; days: number } | { kind: 'overdue' },
): string {
  const { date } = productionDayOf(state.cursor);
  if (opts.kind === 'overdue') return 'Overdue';
  if (opts.kind === 'today') {
    const label = opts.hm.toLowerCase() === 'eod' ? 'EOD' : opts.hm;
    return `Today ${label}`;
  }
  const due = addMinutes(`${date}T12:00`, opts.days * 24 * 60);
  const d = parseTime(due);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** Issues-board title stamp from the cursor — e.g. `08 Jun 2026 · Day shift · 11:30`. */
export function issuesBoardTitleStamp(state: SessionState): string {
  return formatClockShort(state.cursor);
}

export type DerivedShiftStamp = {
  at: SeriesTime;
  kind: 'day' | 'night';
  panel: 'A' | 'B' | 'C' | 'D';
};

/** Last N shift stamps from the anchor series, oldest first. */
export function lastShiftStamps(state: SessionState, count = 14): DerivedShiftStamp[] {
  const anchor = seriesAt('rs-zn-rec-shift', state);
  const points = anchor.points.filter(
    (p): p is { at: SeriesTime; value: number } => p.value !== null,
  );
  return points.slice(-count).map((pt) => {
    const { kind } = shiftOf(pt.at);
    const crew = activeCrewForShift(pt.at, kind);
    return { at: pt.at, kind, panel: CREW_PANEL[crew.crewId] };
  });
}

/** Read a shift-cadence series value at a shift stamp. */
export function shiftValueAt(
  seriesId: string,
  stamp: SeriesTime,
  state: SessionState,
): number | undefined {
  return seriesAt(seriesId, { ...state, cursor: stamp }).latest?.value;
}

/** Tonnes milled for one shift (day or night series). */
export function shiftTonnesAt(stamp: SeriesTime, kind: 'day' | 'night', state: SessionState): number {
  const id = kind === 'day' ? 'rs-milled-day' : 'rs-milled-night';
  return Math.round(shiftValueAt(id, stamp, state) ?? 0);
}

/** Daily mill utilisation (`rs-mill-util`) for the production day of a shift stamp. */
export function shiftMillUtilAt(stamp: SeriesTime, state: SessionState): number {
  return shiftValueAt('rs-mill-util', stamp, state) ?? 0;
}

/** Last N shift-composite stamps for LIMS / assay tables — newest first. */
export function lastCompositeStamps(state: SessionState, count = 6): SeriesTime[] {
  return lastShiftStamps(state, count)
    .map((s) => s.at)
    .reverse();
}

/** Complete production days of the cursor's month (D25): the day count of the
 *  one June-to-date tonnage window, so every run-rate divides the complete
 *  days' tonnage by the same days. The open production day is in no total. */
export function mtdCompleteDays(state: SessionState): number {
  return juneMtdDays(state).length;
}

/** Daily milled tonnes (day + night) for each complete production day 1…last
 *  closed. This is the one June-to-date tonnage function's window (D25: the
 *  measured `rs-ore-td` readings whose day shift has closed), so the cumulative
 *  MTD line, the landing tile, `/cost`, `/planning` and `/reports` cannot
 *  publish two different June tonnages. */
export function mtdDailyTonnes(state: SessionState): number[] {
  return juneMtdDays(state).map((day) => day.dst);
}

/**
 * One plan horizon for the whole route: the daily plan constant and the
 * production days in a month. The MTD plan line, the full-month plan total
 * (`daily × days`), the plan-revision option row and the Manager's escalation
 * card all read these — a second hard-coded month total is a second horizon.
 */
export const MTD_DAILY_PLAN_DST = JUNE_DAILY_PLAN_DST;
export const MONTH_PRODUCTION_DAYS = 30;

/** MTD plan result shared by Superintendent plan tab and Manager escalation card 5. */
export function mtdPlanRevisionRead(state: SessionState): {
  direction: 'above' | 'below';
  pctAbovePlan: number;
  mtdActual: number;
  mtdPlan: number;
  monthPlan: number;
  forecastTonnes: number;
} {
  const mtd = mtdCumulativeFromSeries(state);
  const mtdActual = mtd.actual[mtd.actual.length - 1] ?? 0;
  const mtdPlan = mtd.plan[mtd.plan.length - 1] ?? 0;
  const mtdGap = mtdActual - mtdPlan;
  const pctAbovePlan = mtdPlan > 0 ? (mtdGap / mtdPlan) * 100 : 0;
  const monthPlan = MTD_DAILY_PLAN_DST * MONTH_PRODUCTION_DAYS;
  // D25: the complete days' tonnage over the complete-day count (12,401 ÷ 7 × 30
  // = 53,147 dst at the cursor), never over the open day as well.
  const completeDays = Math.max(1, mtdCompleteDays(state));
  const forecastTonnes = Math.round((mtdActual / completeDays) * MONTH_PRODUCTION_DAYS);
  return {
    direction: mtdGap >= 0 ? 'above' : 'below',
    pctAbovePlan,
    mtdActual,
    mtdPlan,
    monthPlan,
    forecastTonnes,
  };
}

export function planRevisionOptionLabel(direction: 'above' | 'below'): string {
  return direction === 'above' ? 'Plan revision upward' : 'Plan revision downward';
}

/** Cumulative MTD tonnes actual vs plan from the series. */
export function mtdCumulativeFromSeries(state: SessionState): {
  days: string[];
  actual: number[];
  plan: number[];
  label: string;
} {
  const daily = mtdDailyTonnes(state);
  const oreTarget = MTD_DAILY_PLAN_DST;
  let cumulative = 0;
  const actual = daily.map((t) => {
    cumulative += t;
    return cumulative;
  });
  const plan = daily.map((_, i) => oreTarget * (i + 1));
  const days = daily.map((_, i) => String(i + 1));
  // D25: the label names the last complete production day the lines cover.
  const lastDay = juneMtdDays(state).at(-1)?.productionDate ?? productionDayOf(state.cursor).date;
  const d = parseTime(`${lastDay}T12:00`);
  const label = `1 to ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  return { days, actual, plan, label };
}

/** Seven daily sample points from a series — one value per production day. */
export function dailyTrendSlice(
  seriesId: string,
  state: SessionState,
  days = 7,
): { categories: string[]; points: number[]; stamps: SeriesTime[] } {
  const view = seriesAt(seriesId, state);
  const byDay = new Map<string, { at: SeriesTime; value: number }>();
  for (const pt of view.points) {
    if (pt.value === null) continue;
    const { date } = productionDayOf(pt.at);
    const existing = byDay.get(date);
    if (!existing || diffMinutes(pt.at, existing.at) > 0) {
      byDay.set(date, { at: pt.at, value: pt.value });
    }
  }
  const sorted = [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b));
  const slice = sorted.slice(-days);
  const categories = slice.map(([, row]) => formatShiftDateLabel(row.at));
  const points = slice.map(([, row]) => row.value);
  const stamps = slice.map(([, row]) => row.at);
  return { categories, points, stamps };
}
